import {
  FormatCurrency,
  FormatCurrencySymbol,
  useFormatCurrency as useUiFormatCurrency,
} from "@tournated-com/ui";
import { useCallback, useMemo } from "react";

import { useLanguage } from "@/i18n";

export {
  useFormatBytes,
  useFormatCountryName,
  useFormatCurrencyName,
  useFormatNumber,
  useFormatNumberRange,
  useFormatDate,
  useFormatDateRange,
} from "@tournated-com/ui";

// `useFormatCurrency`, `useFormatCurrencySymbol` and `getCurrencyDisplay` below can be dropped
// in favour of the `@tournated-com/ui` exports once it is bumped to the version that ships
// `getCurrencyDisplay` (> 0.0.276).

/**
 * `"symbol"` keeps dollar currencies distinguishable (`HK$`, `MX$`, `CA$`), but renders USD
 * as `US$` outside `en-US`. USD is the dollar users read as a bare `$`, so it uses the
 * narrow symbol instead.
 */
export const getCurrencyDisplay = (
  currency?: string | null
): Intl.NumberFormatOptions["currencyDisplay"] =>
  currency?.toUpperCase() === "USD" ? "narrowSymbol" : "symbol";

/**
 * Wraps the shared UI formatter, which defaults to `currencyDisplay: "narrowSymbol"`.
 * A narrow symbol renders every dollar currency (HKD, MXN, CAD, AUD, …) as a bare
 * `$`, indistinguishable from USD. `"symbol"` keeps it unambiguous for the active
 * locale (`HK$`, `MX$`, `CA$`, `A$`); USD always stays a bare `$` (see `getCurrencyDisplay`).
 * Callers may still override `currencyDisplay` through `options`.
 */
export const useFormatCurrency = (): FormatCurrency => {
  const formatCurrency = useUiFormatCurrency();

  return useCallback<FormatCurrency>(
    (amount, currency, options) =>
      formatCurrency(amount, currency, {
        currencyDisplay: getCurrencyDisplay(currency),
        ...options,
      }),
    [formatCurrency]
  );
};

/**
 * Same as the shared UI hook (locale = the language passed to the UI `ThemeProvider`),
 * but with `getCurrencyDisplay` instead of `"narrowSymbol"` (`HK$`, not `$`; USD stays `$`).
 */
export const useFormatCurrencySymbol = (): FormatCurrencySymbol => {
  const [locale] = useLanguage();
  const cache = useMemo(() => new Map<string, string>(), []);

  return useCallback<FormatCurrencySymbol>(
    (currencyCode) => {
      const key = locale + currencyCode;
      const cached = cache.get(key);
      if (cached !== undefined) return cached;
      try {
        const parts = new Intl.NumberFormat(locale, {
          style: "currency",
          currency: currencyCode,
          currencyDisplay: getCurrencyDisplay(currencyCode),
        }).formatToParts(0);
        const symbol = parts.find((part) => part.type === "currency")?.value ?? currencyCode;
        cache.set(key, symbol);
        return symbol;
      } catch {
        return currencyCode;
      }
    },
    [locale, cache]
  );
};
